import type { HttpResponse } from '@angular/common/http';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import type { Observable } from 'rxjs';
import { catchError, map, of, switchMap, tap, throwError } from 'rxjs';

import { EncryptionService } from './encryption.service';
import { FileService } from './file-access.service';
import { ENVIRONMENT } from '../../enviroment/enviroment';
import type { Profile } from '../models/profile';
import { toCamelCase } from '../shared/utilities/case-conversion';
import { PermissionFacadeService } from '../store/permission/permission-facade.service';
import { ViewsFacade } from '../store/views/views-facade..service';

export type PermissionMap = Record<string, boolean>;
export type RolePermissions = Record<string, PermissionMap>;

@Injectable({
  providedIn: 'root',
})
export class PermissionService {
  permissionFacadeService = inject(PermissionFacadeService);
  http = inject(HttpClient);
  encryptionService = inject(EncryptionService);
  viewsFacade = inject(ViewsFacade);
  fileService = inject(FileService);

  headers!: HttpHeaders;
  profile!: Profile;
  userRolePermissions: RolePermissions = ENVIRONMENT.ANGULAR_APP_USER_ROLE_PERMISSION || {};

  loadPermissions(): Observable<{
    res: HttpResponse<{ data: Profile }>;
    profilePicture: string;
  }> {
    const TOKEN = this.encryptionService.decodeBase64(localStorage.getItem('token'));
    const URL = ENVIRONMENT.ANGULAR_APP_API_URL + 'onboarding/customer/get-user-details';

    const HEADERS = TOKEN ? new HttpHeaders({ authorization: TOKEN }) : new HttpHeaders();
    const OPTIONS = { headers: HEADERS, observe: 'response' as const };

    return this.http.post<{ data: Profile }>(URL, {}, OPTIONS).pipe(
      switchMap((res: HttpResponse<{ data: Profile }>) => {
        this.profile = toCamelCase(res.body!.data);

        this.permissionFacadeService.setPermission(this.profile);

        const PLACEHOLDER = ENVIRONMENT.ANGULAR_APP_DEFAULT_PLACEHOLDER;

        if (!this.profile?.profilePicture) {
          return of({ res, profilePicture: PLACEHOLDER });
        }

        return this.fileService.getSignedUrl$(this.profile.profilePicture).pipe(
          catchError(() => of(PLACEHOLDER)),
          map((url) => ({ res, profilePicture: url }))
        );
      }),
      tap(({ profilePicture }) => {
        this.viewsFacade.setProfileImage(profilePicture);
        this.viewsFacade.setProfile(this.profile);
      }),
      catchError((e) => {
        if (e?.status === 401) {
          localStorage.clear();
          window.location.reload();
        }
        return throwError(() => e);
      })
    );
  }

  getUserRole(): string {
    return this.profile?.Role?.roleName || '';
  }

  getPermissions(): RolePermissions {
    return this.userRolePermissions;
  }
}
